// 三角形グリッドの幾何。**このゲームの土台**なので、レンダリング非依存の純 TS にして
// vitest で固める（[__tests__/geometry.test.ts](__tests__/geometry.test.ts)）。
//
// 原作（`CTriangle:getSidePoints` / `getApexPoints` → `CShapeMgr:_getTriangleAtPoint`）は
// **画面座標**で隣の中心座標を計算し、そこにある三角形を線形探索していた。
// Web 版は**整数グリッド `(r, c)` へ読み替える**（等価で、テストできて、探索が要らない）。
//
// ## 格子
//
// 行 `r`（上から）・列 `c`（左から）。同じ行で ▲ と ▼ が交互に並ぶ:
// **`(r + c)` が偶数なら上向き ▲ / 奇数なら下向き ▼**。
//
// ```
//   c →   0     1     2     3
// r=0    ▲     ▼     ▲     ▼
// r=1    ▼     ▲     ▼     ▲
// ```
//
// ## 隣接は6個で、重みが違う（このゲームの核心）
//
// | 種類 | 個数 | 重み | ガイド色 |
// |---|---|---|---|
// | **辺**で接する | 3 | **+2** | 赤 |
// | **頂点**で接する | 3 | **+1** | 緑 |
//
// 導出根拠（原作の座標式）: 辺長 `s`、重心から頂点まで `hT = s/√3`、重心から底辺まで
// `hB = s/(2√3)`。同じ行の隣は x が ±s/2・y が ±hB ずれる＝**左右の辺接は同じ行**。
// ▲の下の辺接は y−2hB ＝ 1行下。▲の頂点接は y+2hT（1行上）と x±s・y−2hB（**1行下の2列隣**）。

/** 盤面上の1マス。形状の外や盤外は単に存在しない（原作も `nil` を配列へ入れない）。 */
export interface Cell {
  readonly r: number;
  readonly c: number;
}

/** 三角形の向き（原作 `TriangleDirectionType`）。 */
export type Orientation = 'up' | 'down';

/** 隣接の種類。数字の重みが違うので区別する（ガイドの色分けにも使う）。 */
export type Adjacency = 'side' | 'apex';

/** `(r + c)` の偶奇で向きが決まる。 */
export function orientationOf(r: number, c: number): Orientation {
  // `%` は負数で負を返すので `& 1` を使う（盤面の局所座標は負になりうる）。
  return ((r + c) & 1) === 0 ? 'up' : 'down';
}

/**
 * **辺**で接する3個（重み +2）。
 * 左右は必ず同じ行。残る1個は ▲ なら真下、▼ なら真上（底辺を共有する相手）。
 */
export function sideNeighbors(r: number, c: number): Cell[] {
  const dr = orientationOf(r, c) === 'up' ? 1 : -1;
  return [
    { r, c: c - 1 },
    { r, c: c + 1 },
    { r: r + dr, c },
  ];
}

/**
 * **頂点**で接する3個（重み +1）。
 * ▲ なら真上に1個と1行下の2列隣が2個、▼ ならその上下反転。
 */
export function apexNeighbors(r: number, c: number): Cell[] {
  const dr = orientationOf(r, c) === 'up' ? 1 : -1;
  return [
    { r: r - dr, c },
    { r: r + dr, c: c - 2 },
    { r: r + dr, c: c + 2 },
  ];
}

/** 6近傍すべて（辺3個 → 頂点3個の順）。 */
export function neighbors(r: number, c: number): Cell[] {
  return [...sideNeighbors(r, c), ...apexNeighbors(r, c)];
}

/** 6近傍を種類つきで返す（数字の重み付けとガイド描画が同じ順序を見られるように）。 */
export function neighborsWithKind(r: number, c: number): { cell: Cell; kind: Adjacency }[] {
  return [
    ...sideNeighbors(r, c).map((cell) => ({ cell, kind: 'side' as const })),
    ...apexNeighbors(r, c).map((cell) => ({ cell, kind: 'apex' as const })),
  ];
}

/**
 * Map / Set のキー。`(r, c)` は負にもなるので文字列で持つ
 * （`r * 大きい数 + c` のような詰め方は負数と盤面サイズの両方で事故る）。
 */
export function cellKey(r: number, c: number): string {
  return `${r},${c}`;
}

/** `cellKey` の逆。 */
export function parseCellKey(key: string): Cell {
  const [r, c] = key.split(',');
  return { r: Number(r), c: Number(c) };
}

// ---------------------------------------------------------------- 大三角形 → 16分割
//
// 盤面は「**大三角形を16個の小三角形に分割**した単位」の組み合わせ（原作
// `kShapeSideLength = 小辺×4` / `CShapeMgr:_createTriangles16`）。
// 大三角形も同じ規則の格子に載るので、**大の (R, C) から小の (r, c) 群へ展開できる**。
//
// ▲の大三角形 (R, C) は、小三角形の行 `4R .. 4R+3` を占める。
// 上から k 行目（0 起点）には **2k+1 個**が並び、左端の列は `4C - k`
// （＝1行ごとに左へ1つ伸びる）。これで 1+3+5+7 = 16 個。
//
// ▼はその上下反転。占める行は同じ `4R .. 4R+3` だが、上の行ほど幅が広い。

/** 大三角形の1辺に並ぶ小三角形の数（原作 `kShapeSideLength / kTriSideLength`）。 */
export const BIG_TRIANGLE_SIDE = 4;

/**
 * 大三角形 `(R, C)` を構成する小三角形 16 個を返す。
 * 向きは `(R + C)` の偶奇で決まり、**小三角形の向きも同じ規則で自動的に整合する**
 * （下の `expandBigTriangle` が返すセルは `orientationOf` と矛盾しない）。
 */
export function expandBigTriangle(R: number, C: number): Cell[] {
  const n = BIG_TRIANGLE_SIDE;
  const up = orientationOf(R, C) === 'up';
  const cells: Cell[] = [];
  for (let k = 0; k < n; k++) {
    // ▲: 上の行ほど狭い（k 行目に 2k+1 個） / ▼: 上の行ほど広い。
    const width = up ? 2 * k + 1 : 2 * (n - 1 - k) + 1;
    const r = n * R + k;
    // 左端の列。▲ は下へ行くほど左へ、▼ は下へ行くほど右へ寄る。
    const c0 = up ? n * C - k : n * C - (n - 1 - k);
    for (let i = 0; i < width; i++) cells.push({ r, c: c0 + i });
  }
  return cells;
}
